import React, {useEffect, useRef, useState} from 'react';
import {Call} from '@store/call-card/Call';
import User from 'records/User';
import CoachingQuickActions from 'components/pure/call-card/coaching-quick-actions/CoachingQuickActions';
import Button from '@material-ui/core/Button';
import SharingActions from 'actions/sharing/SharingActions';
import {ShareIcon} from '@components/icon/ShareIcon';
import FileUploadCallDetailsActions from 'actions/files/FileUploadCallDetailsActions';
import {CloudIcon} from '@components/icon/CloudIcon';
import {TrashIcon} from '@components/icon/TrashIcon';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import {WhistleIcon} from '@components/icon/WhistleIcon';
import './CallCardHeaderActions.sass';
import {ChevronUpIcon} from '@components/icon/ChevronUpIcon';
import hasFlag from 'utils/hasFlag';
import {featureFlags} from 'utils/const';

interface CallCardHeaderActionsProps {
  call: Call;
  sessionUser: User;
  headerRef: HTMLDivElement | null;
  supportsCrmSync: boolean;
}

export const CallCardHeaderActions: React.FC<CallCardHeaderActionsProps> = ({
  call,
  sessionUser,
  headerRef,
  supportsCrmSync,
}) => {
  const anchor = useRef(null);
  const wrapper = useRef(null);
  const [showMenu, setShowMenu] = useState(false);
  const [headerWidth, setHeaderWidth] = useState(headerRef?.getBoundingClientRect().width ?? 0);

  useEffect(() => {
    if (headerRef) {
      const obs = new ResizeObserver(([header]) => {
        setHeaderWidth(header.contentRect.width);
      });
      obs.observe(headerRef);
      return () => obs.disconnect();
    }
  }, [headerRef]);

  return (
    <div ref={wrapper} className="call-card-header-actions">
      {headerWidth < 800 ? (
        <div className="call-actions-small">
          <Button ref={anchor} variant="outlined" color="primary" onClick={() => setShowMenu(true)}>
            Actions
            <ChevronUpIcon />
          </Button>
          <Menu
            getContentAnchorEl={null}
            anchorOrigin={{horizontal: 'left', vertical: 'bottom'}}
            open={showMenu && !!anchor.current}
            anchorEl={anchor.current}
            onClose={() => setShowMenu(false)}>
            {hasFlag(featureFlags.downloadCall) && (
              <MenuItem
                onClick={() => {
                  setShowMenu(false);
                  window.open(
                    `/api/internal/calls/${call.id}/download`,
                    '_blank',
                    'noopener,noreferrer'
                  );
                }}>
                Download
              </MenuItem>
            )}
            <MenuItem>
              <CoachingQuickActions
                newCallCard
                button={'Coach'}
                fileUploadId={call.id}
                call={call}
                isCoachingRequested={call.coachMe}
                caller={call.caller}
              />
            </MenuItem>
            <MenuItem
              onClick={() => {
                setShowMenu(false);
                SharingActions.showSharingDialog();
              }}>
              Share
            </MenuItem>
            {supportsCrmSync && (
              <MenuItem
                onClick={() => {
                  setShowMenu(false);
                  FileUploadCallDetailsActions.showCRMRecordDialog(call.id);
                }}>
                Sync
              </MenuItem>
            )}
            {sessionUser.isAdmin() && (
              <MenuItem
                onClick={() => {
                  setShowMenu(false);
                  FileUploadCallDetailsActions.showCallDeleteDialog(call.id);
                }}>
                Delete
              </MenuItem>
            )}
          </Menu>
        </div>
      ) : (
        <div className="call-actions-full">
          {hasFlag(featureFlags.downloadCall) && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() =>
                window.open(
                  `/api/internal/calls/${call.id}/download`,
                  '_blank',
                  'noopener,noreferrer'
                )
              }>
              Download Call
            </Button>
          )}
          <CoachingQuickActions
            newCallCard
            button={
              <Button variant="outlined" color="primary">
                <WhistleIcon /> Coach
              </Button>
            }
            fileUploadId={call.id}
            call={call}
            isCoachingRequested={call.coachMe}
            caller={call.caller}
          />
          <Button
            variant="outlined"
            color="primary"
            onClick={() => SharingActions.showSharingDialog()}>
            <ShareIcon /> Share
          </Button>
          {supportsCrmSync && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => FileUploadCallDetailsActions.showCRMRecordDialog(call.id)}>
              <CloudIcon /> Sync to Salesforce
            </Button>
          )}
          {sessionUser.isAdmin() && (
            <Button
              className="trash-button"
              variant="outlined"
              color="primary"
              onClick={() => {
                FileUploadCallDetailsActions.showCallDeleteDialog(call.id);
              }}>
              <TrashIcon tooltip="Delete" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
